import React from 'react';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { MAXIMIZE_DONATIONS_TOOLTIP_CONTENT } from '../../../../../../../CharitableGivingWorkflow/stages/CharitableGiving/steps/DonationAmount/DonationAmount.meta';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { Checkbox } from '../../../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { Icon } from '../../../../../../../CommonComponents/OPL/Icon/Icon';
import { InputNumberMask } from '../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Select } from '../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Tooltip } from '../../../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { CharitableGivingOptions } from '../../../../../api/useGoalsQuery';
import { AssetBucketingSelect } from '../../../../../components/AssetBucketingSelect/AssetBucketingSelect';
import { CharitableGoalDataDTO } from '../../../CharitableGiving.meta';
import { useFormUtils } from '../../../hooks/useFormUtils';
import { FundAccount } from '../../FundAccount/FundAccount';
import { CharityName } from '../../CharityName/CharityName';

interface Props {
  options: CharitableGivingOptions;
  clientId: string;
  hasAssetBucketing: boolean;
}

export const QualifiedCharitableDonations = ({ options, clientId, hasAssetBucketing }: Props) => {
  const { formState, control, register, setValue } = useFormContext<CharitableGoalDataDTO>();

  const { isRecurring, maximizeDonations, yearOptions } = useFormUtils({ options });

  const { donorOptions } = options;

  return (
    <>
      <CharityName options={options} />

      <Controller
        control={control}
        name="donor"
        render={({ field: { value: formValue, onChange, ...rest } }) => (
          <Select
            {...rest}
            isPortal
            label="Donor"
            value={donorOptions.find(({ value }) => value === formValue)}
            onChange={opt => onChange(opt?.value)}
            options={donorOptions}
            formVariant={formState.errors.donor ? 'error' : undefined}
            helperText={formState.errors.donor?.message}
          />
        )}
      />

      <Box
        display="flex"
        flex={1}
        alignItems="start"
        justifyContent="space-between"
        css={`
          gap: 16px;
        `}
      >
        <Box width="100%">
          <Controller
            control={control}
            name="donationStartYear"
            render={({ field: { value, onChange, ref } }) => (
              <Select
                isPortal
                innerRef={ref}
                label={isRecurring ? 'Start Year' : 'Donation Year'}
                value={yearOptions.find(opt => opt.value === value)}
                options={yearOptions}
                onChange={opt => onChange(opt?.value)}
                formVariant={formState.errors.donationStartYear ? 'error' : undefined}
                helperText={formState.errors.donationStartYear?.message}
              />
            )}
          />
        </Box>

        {isRecurring && (
          <Box width="100%">
            <Controller
              control={control}
              name="donationEndYear"
              render={({ field: { value, onChange, ref } }) => (
                <Select
                  isPortal
                  innerRef={ref}
                  label="End Year"
                  value={yearOptions.find(opt => opt.value === value)}
                  options={yearOptions}
                  onChange={opt => onChange(opt?.value)}
                  formVariant={formState.errors.donationEndYear ? 'error' : undefined}
                  helperText={formState.errors.donationEndYear?.message}
                />
              )}
            />
          </Box>
        )}
      </Box>

      <Checkbox {...register('isRecurring')}>Recurring</Checkbox>

      <Controller
        control={control}
        name="donationAmount"
        render={({ field: { onChange, value, ...field } }) => (
          <InputNumberMask
            {...field}
            label="Donation Amount"
            leftIcon="fa fa-dollar-sign"
            innerText="/annually"
            value={value ?? ''}
            allowNegative={false}
            disabled={maximizeDonations === true}
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
            formVariant={formState.errors.donationAmount ? 'error' : undefined}
            helperText={formState.errors.donationAmount?.message}
          />
        )}
      />

      <Box display="flex" alignItems="center">
        <Checkbox
          {...register('maximizeDonations', {
            onChange: evt => {
              if (evt.target.checked) {
                setValue('donationAmount', null, { shouldDirty: true, shouldValidate: true });
              }
            },
          })}
        >
          Maximize Donations
        </Checkbox>

        <Tooltip content={MAXIMIZE_DONATIONS_TOOLTIP_CONTENT}>
          <Icon className="far fa-info-circle" color="neutral.500" fontSize="small" />
        </Tooltip>
      </Box>

      <FundAccount label="Which account(s) will fund this goal?" />

      {hasAssetBucketing && <AssetBucketingSelect clientId={clientId} control={control} />}
    </>
  );
};
